import { twMerge } from "tailwind-merge";
import {
  Card,
  CardContent,
  CardHeader,
  H2,
  H5,
  H6,
} from "@sellar-io/components";
import {
  BuyerProductGetResponseType,
  StockListResponseType,
  SupplierGetResponseType,
  SupplierStorefrontV2GetResponseType,
} from "@sellar-io/api-v2/types";
import { calculateDeliveryOffers } from "@sellar-io/common";
import { InfiniteData } from "@tanstack/react-query";
import { ReactElement } from "react";
import { SpinnerLegacy, StockIndicator } from "../../../../components";
import { BadgePurple } from "../../../../components/badge/Badge";
import { OrderDiscountSummary } from "../../../../components/discountSummary/OrderDiscountSummary";
import {
  Dialog,
  DialogNavigation,
  DialogContent,
} from "../../../../components/modal/Dialog";
import {
  SkeletonContainer,
  SkeletonText,
} from "../../../../components/skeleton/Skeleton";
import { useBasketContext, useSellarUser } from "../../../../hooks";
import { useBuyerProduct } from "../../../../hooks/api/v2/product/useBuyerProduct";
import { useStockList } from "../../../../hooks/api/v2/stockList/useStockList";
import { useSupplier } from "../../../../hooks/api/v2/supplier/useSupplier";
import { useSupplierStorefrontV2 } from "../../../../hooks/api/v2/supplier/useSupplierStorefrontV2";
import { useSellarCurrentBusiness } from "../../../../hooks/useSellarCurrentBusiness.shared";

import { OverallChampionBadge } from "../../../spotlight/components/SharedCardComponents/OverallChampionBadge";
import { productAwardData } from "../../../spotlight/components/SpotlightProductCard.shared";
import { StorefrontOrderRulesCard } from "../../../stockList/categoryPage/Storefront/StorefrontCards/StorefrontOrderRulesCard";
import { StorefrontProductCard } from "../../../stockList/categoryPage/Storefront/StorefrontLayout/StorefrontProductCard";
import { DiscountedPrice } from "../../../stockList/DiscountedPrice";
import { useStockListOrderSummaryProps } from "../../../stockList/useStockListOrderSummaryProps";
import { DiscoveryProductDialogCta } from "./DiscoveryProductDialogCta";
import { MaxOrderQuantityBadge } from "../../../../components/badge/MaxOrderQuantityBadge";
import { AvailableFromBadge } from "../../../../components/badge/AvailableFromBadge";
import { DietaryDisplayList } from "../../../../components/dietaryDisplayList/DietaryDisplayList";
import { DiscoveryDialogFulfillmentTypeInformation } from "./DiscoveryDialogFulfillmentTypeInformation";

type DiscoveryProductDialogProps = {
  active: boolean;
  onDismiss: () => void;
  productId?: number;
  supplierId?: number;
  setBestPricePromiseDialogActive?: (active: boolean) => void;
  showAwardBadge?: boolean;
};

const isFreeFrom = ({
  property,
  product,
}: {
  property: "glutenFree" | "lactoseFree" | "vegan";
  product: {
    glutenFree: boolean | null;
    lactoseFree: boolean | null;
    vegan: boolean | null;
    Parent: {
      glutenFree: boolean | null;
      lactoseFree: boolean | null;
      vegan: boolean | null;
    } | null;
  };
}) => {
  return (
    !!product[property] ||
    (product[property] === null && !!product.Parent?.[property])
  );
};

/**
 * @deprecated Please use the newer {@link DiscoveryV2ProductDialog}
 */
export const DiscoveryProductDialog = (props: DiscoveryProductDialogProps) => {
  return <DiscoveryParentProductDialog {...props} />;
};

const DiscoveryParentProductDialog = ({
  active,
  onDismiss,
  productId,
  supplierId,
  setBestPricePromiseDialogActive,
  showAwardBadge,
}: {
  active: boolean;
  onDismiss: () => void;
  productId?: number;
  supplierId?: number;
  setBestPricePromiseDialogActive?: (active: boolean) => void;
  showAwardBadge?: boolean;
}): ReactElement | null => {
  const { business } = useSellarCurrentBusiness();
  const productQuery = useBuyerProduct(productId ?? 0, {
    enabled: active && !!productId && !isNaN(productId),
  });

  const supplierQuery = useSupplier(supplierId, {
    enabled: active && !!supplierId && !isNaN(supplierId),
  });

  const productsListQuery = useStockList(
    {
      ParentId: productId,
      active: true,
      SupplierId: productQuery.data?.Supplier.id ?? 0,
      RetailerId: business?.Retailer?.id ?? undefined,
    },
    { enabled: !!productQuery.data?.Supplier.id },
  );

  const supplierStorefrontQuery = useSupplierStorefrontV2(
    productQuery.data?.SupplierId ?? 0,
    {},
    {
      enabled: !!productQuery.data?.SupplierId,
    },
  );

  const displayContent =
    supplierQuery.data &&
    supplierStorefrontQuery.data &&
    productQuery.data &&
    productsListQuery.data?.pages;

  return (
    <Dialog
      active={active}
      onDismiss={onDismiss}
      fullHeight={true}
      className="w-full max-w-[1000px] rounded-none md:rounded-lg"
    >
      <DialogNavigation
        leftIcon={"CHEVRON_DOWN"}
        onClickLeftIcon={onDismiss}
        className={"z-10 pb-0"}
        title={productQuery.data?.name ?? ""}
      />
      {displayContent ? (
        <>
          <DialogContent
            className={
              "mx-auto mb-8 grid w-full content-start gap-y-2 md:grid-cols-12"
            }
          >
            <DiscoveryParentProductDialogContent
              product={productQuery.data}
              productList={productsListQuery.data}
              supplier={supplierQuery.data}
              supplierStorefront={supplierStorefrontQuery.data}
              setBestPricePromiseDialogActive={setBestPricePromiseDialogActive}
              showAwardBadge={showAwardBadge}
            />
          </DialogContent>
          <DiscoveryProductDialogCta
            product={productsListQuery.data?.pages[0]?.[0]}
            supplier={supplierQuery.data}
            connection={supplierStorefrontQuery.data.SupplierRetailerConnection}
          />
        </>
      ) : (
        <DialogContent className="flex min-h-0 flex-1 items-center justify-center">
          <SpinnerLegacy />
        </DialogContent>
      )}
    </Dialog>
  );
};

export const DiscoveryParentProductDialogContent = ({
  product,
  productList,
  supplier,
  supplierStorefront,
  setBestPricePromiseDialogActive,
  showAwardBadge,
}: {
  product: BuyerProductGetResponseType;
  productList: InfiniteData<StockListResponseType> | undefined;
  supplier: SupplierGetResponseType;
  supplierStorefront: SupplierStorefrontV2GetResponseType;
  setBestPricePromiseDialogActive?: (active: boolean) => void;
  showAwardBadge?: boolean;
}) => {
  const { user } = useSellarUser();
  const { business } = useSellarCurrentBusiness();
  const [{ order }] = useBasketContext();
  const priceTierDiscounts =
    supplierStorefront.VolumeDiscounts?.PriceTierDiscounts ?? [];

  const priceTierOrderRules =
    supplierStorefront.FulfillmentType.OrderRules?.PriceTierOrderRules ??
    supplierStorefront.OrderRules?.PriceTierOrderRules ??
    [];

  const minimumOrderPrice =
    supplierStorefront.FulfillmentType.OrderRules?.minimumOrderPrice ??
    supplierStorefront.OrderRules?.minimumOrderPrice ??
    0;

  const freeDeliveryBasketSubtotal =
    supplierStorefront.FulfillmentType.freeDeliveryBasketSubtotal ?? null;

  const isLoggedIn = !!user && !!business;

  const {
    apiValues: { price: orderTotal },
  } = useStockListOrderSummaryProps({
    order,
    deliveryChargeRules: supplierStorefront.FulfillmentType.DeliveryChargeRules,
    deliveryCharge: supplierStorefront.FulfillmentType.charge,
    fulfillmentTypeContainerDeliveryCharges:
      supplierStorefront.FulfillmentType
        .FulfillmentTypeContainerDeliveryCharges,
    priceTierDiscounts,
    caseSizeRuleList: supplierStorefront.CaseSizeRules.list,
    priceTierOrderRules,
    minimumOrderPrice,
    containerTypeDeliveryChargeRules:
      supplierStorefront.FulfillmentType.ContainerTypeDeliveryChargeRules,
    freeDeliveryBasketSubtotal,
  });

  const awardData = productAwardData.find((a) => a.productId === product.id);
  const showOverallChampionBadge =
    showAwardBadge && awardData && awardData.overallCategoryMedal !== null;

  return (
    <>
      <div className="md:col-span-6">
        <StorefrontProductCard
          setBestPricePromiseDialogActive={setBestPricePromiseDialogActive}
          item={product}
          supplier={supplier}
          showAwardBadge={showAwardBadge}
          topBadge={
            showOverallChampionBadge ? (
              <OverallChampionBadge
                tier={awardData.overallCategoryMedal!}
                type={awardData.overallCategory!}
              />
            ) : undefined
          }
          className="z-0 mb-4 md:sticky md:top-[calc(var(--nav-height))] md:col-span-4 md:max-h-[calc(100vh_-_16px_-_var(--nav-height))] md:overflow-auto"
        ></StorefrontProductCard>
      </div>
      <div className="md:col-span-6 md:overflow-auto md:px-2">
        <div className={"flex flex-col gap-2"}>
          {productList?.pages[0]?.map((product, i) => {
            return (
              <Card key={i} className="bg-white">
                <CardHeader
                  className={twMerge("bg-white", "rounded-t border p-4")}
                >
                  <div className={"flex flex-col gap-2"}>
                    <div className="flex flex-col">
                      <H2>{product.name?.split(" • ").slice(1).join(" • ")}</H2>
                      {product.ProductPriceTier?.discountable === false && (
                        <H6>This item is not eligible for volume discounts.</H6>
                      )}
                    </div>
                    <div className="flex flex-col gap-2">
                      <div className="flex w-full flex-wrap gap-1">
                        {product.type !== "badge" && (
                          <DietaryDisplayList
                            dietaryRequirements={{
                              VG: isFreeFrom({ property: "vegan", product }),
                              LF: isFreeFrom({
                                property: "lactoseFree",
                                product,
                              }),
                              GF: isFreeFrom({
                                property: "glutenFree",
                                product,
                              }),
                            }}
                            mode="badge"
                          />
                        )}
                        <StockIndicator
                          stock={product.availableStock}
                          showStock={supplier.showStock}
                          canBackorder={product.canBackorder}
                        />
                        {!!product.maxOrderQuantity && (
                          <MaxOrderQuantityBadge
                            maxQuantity={product.maxOrderQuantity}
                          />
                        )}
                      </div>
                      {!!product.availableFrom && (
                        <AvailableFromBadge
                          availableFrom={product.availableFrom}
                        />
                      )}
                    </div>
                  </div>
                </CardHeader>
                <CardContent className={"rounded-b border border-t-0 p-4"}>
                  <div className="flex flex-col gap-4">
                    <div className="flex flex-wrap items-center justify-between gap-2 md:flex-row">
                      <div className="grid w-full grid-cols-6">
                        <div className={"col-span-3"}>
                          <H5>Unit price (ex. VAT)</H5>
                        </div>
                        {business && product.ProductPriceTier ? (
                          <div className="col-span-3 flex justify-end">
                            <H5 className="select-text">
                              {product.ProductPriceTier.price >= 0 && (
                                <DiscountedPrice
                                  productPriceTier={product.ProductPriceTier}
                                />
                              )}
                            </H5>
                          </div>
                        ) : (
                          <>
                            {isLoggedIn ? (
                              <>
                                <div className={"col-span-3 flex justify-end"}>
                                  <H5>
                                    £{" "}
                                    {supplierStorefront.SupplierRetailerConnection
                                      ? "Price not set"
                                      : "Pricing on request"}
                                  </H5>
                                </div>
                              </>
                            ) : (
                              <div
                                className={
                                  "col-span-3 inline-flex justify-end gap-1"
                                }
                              >
                                <p>£</p>{" "}
                                <SkeletonContainer>
                                  <SkeletonText
                                    className={"h-[20px] w-[75px]"}
                                  />
                                </SkeletonContainer>
                              </div>
                            )}
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
          {priceTierDiscounts.length > 0 && (
            <Card
              className={twMerge(
                "bg-white",
                "flex flex-col gap-2 rounded-sm border p-4",
              )}
            >
              <BadgePurple icon={"TAG_MULTIPLE_OUTLINE"}>
                Volume discounts
              </BadgePurple>
              <OrderDiscountSummary
                discounts={priceTierDiscounts}
                currency={supplier.currency}
                hideInfo={true}
                caseSizeRuleList={supplierStorefront.CaseSizeRules.list}
              />
            </Card>
          )}
          {supplierStorefront && (
            <DiscoveryDialogFulfillmentTypeInformation
              supplierStorefront={supplierStorefront}
              supplierId={supplier.id}
              productItem={product}
            />
          )}
          {calculateDeliveryOffers.length && (
            <StorefrontOrderRulesCard
              className="snap-center rounded-md border"
              supplierStorefront={supplierStorefront}
              order={order}
              orderTotal={orderTotal}
            />
          )}
        </div>
      </div>
    </>
  );
};
